/**
 * 💕 COUPLE MODE - Composant pour les couples qui cherchent des amis
 * 
 * Mode Mozart/Jobs/Tesla/Da Vinci
 * 
 * Permet aux couples de :
 * - Créer un profil couple
 * - Trouver des amis couples ou solo
 * - Organiser/rejoindre des soirées
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Users, Sparkles, Wine, Music, Camera, MapPin, ChevronRight, Check, X, ImagePlus, ChevronLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CouplePhotosUploader, CouplePhotoGallery } from './features/CouplePhotosUploader';

// 🎯 Types de relations
export const RELATIONSHIP_STATUS = {
  single: { label: 'Célibataire', emoji: '💜', description: 'Je cherche l\'amour' },
  couple: { label: 'En couple', emoji: '💕', description: 'On cherche des amis' },
  open: { label: 'Relation libre', emoji: '💫', description: 'Ouvert à tout' },
};

// 🎉 Intérêts pour les couples
export const COUPLE_INTERESTS = [
  { id: 'dinner', emoji: '🍷', label: 'Dîners entre amis' },
  { id: 'games', emoji: '🎲', label: 'Soirées jeux' },
  { id: 'travel', emoji: '✈️', label: 'Voyages à plusieurs' },
  { id: 'sport', emoji: '🏃', label: 'Sport ensemble' },
  { id: 'culture', emoji: '🎭', label: 'Sorties culturelles' },
  { id: 'party', emoji: '🎉', label: 'Soirées festives' },
  { id: 'nature', emoji: '🌿', label: 'Balades nature' },
  { id: 'cooking', emoji: '👨‍🍳', label: 'Ateliers cuisine' },
  { id: 'music', emoji: '🎵', label: 'Concerts & festivals' },
  { id: 'chill', emoji: '🛋️', label: 'Apéros tranquilles' },
  { id: 'adventure', emoji: '🚀', label: 'Activités sensations' },
  { id: 'wellness', emoji: '🧘', label: 'Bien-être & spa' },
];

/**
 * 💕 RelationshipSelector - Sélection du statut relationnel
 */
export const RelationshipSelector = ({
  value = 'single',
  onChange,
  isDarkMode = true,
}) => {
  return (
    <div className="space-y-3">
      <Label className={isDarkMode ? 'text-white' : ''}>
        💜 Ton statut relationnel
      </Label>
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(RELATIONSHIP_STATUS).map(([key, status]) => (
          <motion.button
            key={key}
            onClick={() => onChange(key)}
            className={`
              p-3 rounded-xl border-2 transition-all text-center
              ${value === key
                ? isDarkMode
                  ? 'border-pink-500 bg-pink-500/20'
                  : 'border-pink-500 bg-pink-50'
                : isDarkMode
                  ? 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }
            `}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="text-2xl mb-1">{status.emoji}</div>
            <div className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {status.label}
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

/**
 * 💕 CoupleProfileSetup - Configuration du profil couple
 */
export const CoupleProfileSetup = ({
  partnerName = '',
  coupleInterests = [],
  couplePhotos = [],
  coupleAvatar = null,
  lookingForFriends = true,
  customerId,
  onUpdate,
  isDarkMode = true,
}) => {
  const [localPartnerName, setLocalPartnerName] = useState(partnerName);
  const [localInterests, setLocalInterests] = useState(coupleInterests);
  const [localLookingForFriends, setLocalLookingForFriends] = useState(lookingForFriends);
  const [showPhotos, setShowPhotos] = useState(false);

  const toggleInterest = (interestId) => {
    const newInterests = localInterests.includes(interestId)
      ? localInterests.filter(i => i !== interestId)
      : [...localInterests, interestId].slice(0, 6); // Max 6 intérêts
    setLocalInterests(newInterests);
    onUpdate?.({ coupleInterests: newInterests });
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="space-y-4"
    >
      {/* Nom du/de la partenaire */}
      <div className="space-y-2">
        <Label className={isDarkMode ? 'text-white' : ''}>
          💕 Prénom de ton/ta partenaire
        </Label>
        <Input
          value={localPartnerName}
          onChange={(e) => {
            setLocalPartnerName(e.target.value);
            onUpdate?.({ partnerName: e.target.value });
          }}
          placeholder="Ex: Julie, Thomas..."
          className={isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : ''}
        />
      </div>

      {/* Ce qu'on cherche */}
      <div className="space-y-2">
        <Label className={isDarkMode ? 'text-white' : ''}>
          🎯 Vous cherchez...
        </Label>
        <div className="flex gap-2">
          <motion.button
            onClick={() => {
              setLocalLookingForFriends(true);
              onUpdate?.({ lookingForFriends: true });
            }}
            className={`
              flex-1 p-3 rounded-xl border-2 transition-all
              ${localLookingForFriends
                ? isDarkMode
                  ? 'border-purple-500 bg-purple-500/20'
                  : 'border-purple-500 bg-purple-50'
                : isDarkMode
                  ? 'border-gray-700 bg-gray-800/50'
                  : 'border-gray-200 bg-white'
              }
            `}
            whileTap={{ scale: 0.98 }}
          >
            <Users className={`w-5 h-5 mx-auto mb-1 ${localLookingForFriends ? 'text-purple-500' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            <div className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Des amis couples
            </div>
          </motion.button>
          <motion.button
            onClick={() => {
              setLocalLookingForFriends(false);
              onUpdate?.({ lookingForFriends: false });
            }}
            className={`
              flex-1 p-3 rounded-xl border-2 transition-all
              ${!localLookingForFriends
                ? isDarkMode
                  ? 'border-pink-500 bg-pink-500/20'
                  : 'border-pink-500 bg-pink-50'
                : isDarkMode
                  ? 'border-gray-700 bg-gray-800/50'
                  : 'border-gray-200 bg-white'
              }
            `}
            whileTap={{ scale: 0.98 }}
          >
            <Sparkles className={`w-5 h-5 mx-auto mb-1 ${!localLookingForFriends ? 'text-pink-500' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            <div className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Ouvert à tout 😏
            </div>
          </motion.button>
        </div>
      </div>

      {/* Intérêts du couple */}
      <div className="space-y-2">
        <Label className={isDarkMode ? 'text-white' : ''}>
          🎉 Vos activités préférées en couple ({localInterests.length}/6)
        </Label>
        <div className="grid grid-cols-3 gap-2">
          {COUPLE_INTERESTS.map((interest) => {
            const isSelected = localInterests.includes(interest.id);
            return (
              <motion.button
                key={interest.id}
                onClick={() => toggleInterest(interest.id)}
                className={`
                  p-2 rounded-lg border transition-all text-center
                  ${isSelected
                    ? isDarkMode
                      ? 'border-pink-500 bg-pink-500/20'
                      : 'border-pink-500 bg-pink-50'
                    : isDarkMode
                      ? 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                  }
                `}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="text-lg">{interest.emoji}</div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {interest.label}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* 📸 Section Photos du couple */}
      <div className="space-y-2">
        <motion.button
          onClick={() => setShowPhotos(!showPhotos)}
          className={`
            w-full p-4 rounded-xl border-2 border-dashed flex items-center justify-between
            ${isDarkMode 
              ? 'border-purple-500/50 bg-purple-500/10 hover:bg-purple-500/20' 
              : 'border-purple-300 bg-purple-50 hover:bg-purple-100'
            }
            transition-colors
          `}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <div className="flex items-center gap-3">
            <ImagePlus className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            <div className="text-left">
              <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                📸 Photos de couple
              </div>
              <div className={`text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                Ajoutez jusqu&apos;à 6 photos de vous deux
              </div>
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 transition-transform ${showPhotos ? 'rotate-90' : ''} ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
        </motion.button>

        <AnimatePresence>
          {showPhotos && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}
            >
              <CouplePhotosUploader
                photos={couplePhotos}
                coupleAvatar={coupleAvatar}
                partnerName={localPartnerName}
                customerId={customerId}
                onPhotosChange={(photos) => onUpdate?.({ couplePhotos: photos })}
                onAvatarChange={(avatar) => onUpdate?.({ coupleAvatar: avatar })}
                isDarkMode={isDarkMode}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

/**
 * 💕 CoupleCard - Carte d'affichage d'un couple dans la découverte
 */
export const CoupleCard = ({
  profile,
  onLike,
  onPass,
  onMessage,
  isDarkMode = true,
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  const displayName = profile.partnerName 
    ? `${profile.displayName} & ${profile.partnerName}`
    : profile.displayName;

  // Toutes les photos disponibles
  const allPhotos = [
    profile.coupleAvatar,
    ...(profile.couplePhotos || []),
    profile.avatarImage
  ].filter(Boolean);

  const nextPhoto = () => {
    if (allPhotos.length > 1) {
      setCurrentPhotoIndex((prev) => (prev + 1) % allPhotos.length);
    }
  };

  const prevPhoto = () => {
    if (allPhotos.length > 1) {
      setCurrentPhotoIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length);
    }
  };

  return (
    <motion.div
      className={`
        relative rounded-2xl overflow-hidden shadow-xl
        ${isDarkMode ? 'bg-gray-800' : 'bg-white'}
      `}
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
    >
      {/* Badge Couple */}
      <div className="absolute top-3 left-3 z-10">
        <div className="px-3 py-1 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold flex items-center gap-1">
          <Users className="w-3 h-3" />
          Couple 💕
        </div>
      </div>

      {/* Indicateurs de photos */}
      {allPhotos.length > 1 && (
        <div className="absolute top-3 right-3 z-10 flex gap-1">
          {allPhotos.map((_, idx) => (
            <div 
              key={idx}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentPhotoIndex 
                  ? 'bg-white w-4' 
                  : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      )}

      {/* Photo/Avatar avec navigation */}
      <div className="relative h-72 bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center">
        {allPhotos.length > 0 ? (
          <>
            <motion.img 
              key={currentPhotoIndex}
              src={allPhotos[currentPhotoIndex]} 
              alt={displayName}
              className="w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            />
            
            {/* Boutons de navigation */}
            {allPhotos.length > 1 && (
              <>
                <button
                  onClick={prevPhoto}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </>
        ) : (
          <div className="text-6xl">💕</div>
        )}
      </div>

      {/* Miniatures des photos */}
      {allPhotos.length > 1 && (
        <div className="flex gap-1 p-2 overflow-x-auto scrollbar-hide">
          {allPhotos.map((photo, idx) => (
            <motion.button
              key={idx}
              onClick={() => setCurrentPhotoIndex(idx)}
              className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentPhotoIndex 
                  ? 'border-pink-500 scale-105' 
                  : 'border-transparent opacity-70 hover:opacity-100'
              }`}
              whileTap={{ scale: 0.95 }}
            >
              <img src={photo} alt="" className="w-full h-full object-cover" />
            </motion.button>
          ))}
        </div>
      )}

      {/* Infos */}
      <div className="p-4">
        <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {displayName}
        </h3>
        
        {profile.city && (
          <p className={`text-sm flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <MapPin className="w-3 h-3" />
            {profile.city}
          </p>
        )}

        {profile.bio && (
          <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {profile.bio}
          </p>
        )}

        {/* Intérêts */}
        {profile.coupleInterests?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {profile.coupleInterests.slice(0, 4).map(interestId => {
              const interest = COUPLE_INTERESTS.find(i => i.id === interestId);
              return interest ? (
                <span 
                  key={interestId}
                  className={`text-xs px-2 py-1 rounded-full ${
                    isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {interest.emoji} {interest.label}
                </span>
              ) : null;
            })}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 pt-0 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPass}
          className="flex-1"
        >
          <X className="w-4 h-4 mr-1" />
          Passer
        </Button>
        <Button
          size="sm"
          onClick={onLike}
          className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:opacity-90"
        >
          <Heart className="w-4 h-4 mr-1" />
          On adore !
        </Button>
      </div>
    </motion.div>
  );
};

/**
 * 🎉 CoupleEventCard - Carte pour les événements couples
 */
export const CoupleEventCard = ({
  event,
  onJoin,
  isDarkMode = true,
}) => {
  return (
    <motion.div
      className={`
        rounded-xl overflow-hidden border
        ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
      `}
      whileHover={{ scale: 1.02 }}
    >
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Wine className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {event.title}
            </h4>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {event.date} • {event.location}
            </p>
          </div>
          <div className="text-right">
            <div className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {event.couples}/{event.maxCouples}
            </div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              couples
            </div>
          </div>
        </div>
        
        {event.description && (
          <p className={`text-sm mt-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {event.description}
          </p>
        )}

        <Button
          onClick={onJoin}
          className="w-full mt-3 bg-gradient-to-r from-purple-500 to-pink-500"
          size="sm"
        >
          <Users className="w-4 h-4 mr-2" />
          Rejoindre ({event.couples}/{event.maxCouples} couples)
        </Button>
      </div>
    </motion.div>
  );
};

export default CoupleProfileSetup;
